import React, { useEffect, useState } from 'react'
import { RangeKeyDict } from 'react-date-range'
import dayjs, { Dayjs } from 'dayjs'
import { DATE_WITH_MONTH_NAME_FORMAT } from 'constant/date'
import { LeftMenuPropsType } from './components/LeftMenu/LeftMenu'

type DateRangeSelectedType = {
  from: Date | null
  to: Date | null
}

type MenuSelectedType =
  | 'none'
  | 'today'
  | 'yesterday'
  | 'lastWeek'
  | 'lastMonth'
  | 'selectWeekly'
  | 'selectMonthly'
  | 'selectDate'

export type DateSingleType = {
  dateType: 'single'
  value?: Date | null
  leftMenuProp?: ObjectNullType
  defaultSelectedMenu?: Exclude<MenuSelectedType, 'none'>
  dateDisplayFormat?: string
  dateDisplayFormatFrom?: string
  onChange?: (value: Date | null) => void
  customSelectWeeklyRangeOnChange?: undefined
}

export type DateRangeType = {
  dateType: 'range'
  value?: [Date | null, Date | null]
  leftMenuProp?: {
    disabledMenu?: LeftMenuPropsType['disabledMenu']
  }
  dateDisplayFormat?: string
  dateDisplayFormatFrom?: string
  defaultSelectedMenu?: Exclude<MenuSelectedType, 'none'>
  onChange?: (value: [Date | null, Date | null]) => void
  customSelectWeeklyRangeOnChange?: (value: RangeKeyDict) => DateRangeSelectedType
}

export type UseDatePickerType = (DateRangeType | DateSingleType) & {
  monthPickerOnly?: boolean
}

const currentYear = dayjs().get('year')

const dateDefaultValue = {
  today: dayjs(),
  yesterday: dayjs().add(-1, 'day'),
  lastWeek: dayjs().add(-6, 'day'),
  lastMonth: dayjs().add(-30, 'day'),
}

type SelectedResultType = { selected: Dayjs | null; dateFormatted: string; valid?: boolean }

function getSelectedFromDefaultMenu(
  menuSelected: MenuSelectedType,
  dateDisplayFormat: string,
): SelectedResultType | null {
  if (!Object.keys(dateDefaultValue).includes(menuSelected)) return null
  const key = menuSelected as 'today' | 'yesterday'
  const selected = dateDefaultValue[key]
  return { selected, dateFormatted: selected.format(dateDisplayFormat) }
}

function getSelectedFromDatePicker(
  menuSelected: MenuSelectedType,
  dateSelected: Date | null | undefined,
  minDate: Date | undefined,
  dateDisplayFormat: string,
): SelectedResultType | null {
  if (menuSelected !== 'selectDate') return null
  let val = dateSelected
  if (minDate) {
    const minDateTime = minDate.getTime()
    const dateSelectedTime = dateSelected?.getTime() ?? minDateTime
    val = dateSelectedTime <= minDateTime ? minDate : dateSelected
  }
  const selected = dayjs(val)
  return { selected, dateFormatted: selected.format(dateDisplayFormat) }
}

function getSelectedFromMonthPicker(
  menuSelected: MenuSelectedType,
  monthSelected: number | null,
  yearSelected: number,
  monthPickerOnly: boolean | undefined,
  minDate: Date | undefined,
  maxDate: Date | undefined,
  dateDisplayFormat: string,
): SelectedResultType | null {
  if (menuSelected !== 'selectMonthly' || !monthPickerOnly || monthSelected === null) return null
  const month = dayjs().year(yearSelected).month(monthSelected)
  if (minDate && month.startOf('month').isBefore(dayjs(minDate).startOf('month')))
    return { selected: null, dateFormatted: '', valid: false }
  if (maxDate && month.endOf('month').isAfter(dayjs(maxDate).endOf('month')))
    return { selected: null, dateFormatted: '', valid: false }
  return { selected: month, dateFormatted: month.format(dateDisplayFormat) }
}

function applySubmitResult(
  result: SelectedResultType,
  setDate: (dateFormatted: string) => void,
  onChange: ((value: Date | null) => void) | undefined,
  dateType: 'single' | 'range',
): boolean {
  if (result.valid === false) return false
  setDate(result.dateFormatted)
  if (dateType === 'single' && onChange) onChange(result.selected?.toDate() ?? null)
  return true
}

function useDatePicker({
  dateType,
  value,
  disabled,
  onChange,
  minDate,
  maxDate,
  defaultSelectedMenu,
  leftMenuProp,
  dateDisplayFormat = DATE_WITH_MONTH_NAME_FORMAT,
  dateDisplayFormatFrom,
  customSelectWeeklyRangeOnChange,
  monthPickerOnly,
}: UseDatePickerType & {
  disabled?: boolean
  minDate?: Date
  maxDate?: Date
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [menuSelected, setMenuSelected] = useState<MenuSelectedType>('none')
  const [dateSelected, setDateSelected] = useState<Date | null>()
  const [dateRangeSelected, setDateRangeSelected] = useState<DateRangeSelectedType>({
    from: null,
    to: null,
  })
  const [yearSelected, setYearSelected] = useState<number>(currentYear)
  const [monthSelected, setMonthSelected] = useState<number | null>(null)
  const [date, setDate] = useState('')

  const isOpen = !!anchorEl
  const showDate =
    menuSelected === 'selectDate' ||
    menuSelected === 'selectWeekly' ||
    menuSelected === 'selectMonthly'

  const handleOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget)
      if (monthPickerOnly) {
        setMenuSelected('selectMonthly')
        const val = dateType === 'single' ? value : (value as [Date | null, Date | null])?.[0]
        if (val) {
          const d = dayjs(val)
          setYearSelected(d.year())
          setMonthSelected(d.month())
        } else {
          setMonthSelected(null)
          setYearSelected(currentYear)
        }
      } else {
        setMenuSelected(defaultSelectedMenu || 'selectDate')
        setMonthSelected(null)
        setYearSelected(currentYear)
      }
    }
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelectMenu = (selected: MenuSelectedType) => {
    if (selected === menuSelected) return

    const disabledMenuList = leftMenuProp?.disabledMenu || []
    if (!disabledMenuList.includes(selected as Exclude<MenuSelectedType, 'none'>)) {
      setMenuSelected(selected)
      setDateRangeSelected({ from: null, to: null })
      setDateSelected(null)
      setMonthSelected(null)
      setYearSelected(currentYear)
    }
  }

  const handleSelectDate = (item: Date) => {
    setDateSelected(item || null)
  }

  const handleSelectDateRange = (item: RangeKeyDict) => {
    if (menuSelected === 'selectWeekly') {
      const lastWeek = dayjs(item.selection.startDate).add(-6, 'day')
      let selectedDateRangeData: DateRangeSelectedType = {
        from: lastWeek.toDate() || null,
        to: item.selection.startDate || null,
      }

      if (typeof customSelectWeeklyRangeOnChange === 'function') {
        selectedDateRangeData = customSelectWeeklyRangeOnChange(item)
      }

      setDateRangeSelected(selectedDateRangeData)
    }
    if (menuSelected === 'selectDate') {
      setDateRangeSelected({
        from: item.selection.startDate || null,
        to: item.selection.endDate || null,
      })
    }
  }

  const handleReset = () => {
    handleClose()
    setDate('')

    if (dateType === 'range' && onChange) onChange([null, null])
    if (dateType === 'single' && onChange) onChange(null)
  }

  const handleSubmitSingle = (): boolean => {
    const singleOnChange =
      dateType === 'single' ? (onChange as (v: Date | null) => void) : undefined
    const defaultResult = getSelectedFromDefaultMenu(menuSelected, dateDisplayFormat)
    if (defaultResult) return applySubmitResult(defaultResult, setDate, singleOnChange, dateType)

    const datePickerResult = getSelectedFromDatePicker(
      menuSelected,
      dateSelected,
      minDate,
      dateDisplayFormat,
    )
    if (datePickerResult)
      return applySubmitResult(datePickerResult, setDate, singleOnChange, dateType)

    const monthResult = getSelectedFromMonthPicker(
      menuSelected,
      monthSelected,
      yearSelected,
      monthPickerOnly,
      minDate,
      maxDate,
      dateDisplayFormat,
    )
    if (monthResult) return applySubmitResult(monthResult, setDate, singleOnChange, dateType)

    if (singleOnChange) singleOnChange(null)
    return true
  }

  const handleSubmitRange = () => {
    let selected: [Date, Date] = [new Date(), new Date()]
    const fromFormat = dateDisplayFormatFrom || dateDisplayFormat

    if (Object.keys(dateDefaultValue).includes(menuSelected)) {
      const key = menuSelected as 'lastWeek' | 'lastMonth'

      const from = dateDefaultValue[key]
      const to = dateDefaultValue.today

      const fromFormated = from.format(fromFormat)
      const toFormated = to.format(dateDisplayFormat)

      selected = [from.toDate(), to.toDate()]
      setDate(`${fromFormated} - ${toFormated}`)
    } else if (menuSelected === 'selectDate' || menuSelected === 'selectWeekly') {
      const from = dayjs(dateRangeSelected.from)
      const to = dayjs(dateRangeSelected.to)

      const fromFormated = from.format(fromFormat)
      const toFormated = to.format(dateDisplayFormat)

      selected = [from.toDate(), to.toDate()]
      setDate(`${fromFormated} - ${toFormated}`)
    } else if (menuSelected === 'selectMonthly' && monthSelected !== null) {
      const month = dayjs().year(yearSelected).month(monthSelected)

      const from = month.startOf('month')
      const to = month.endOf('month')

      const fromFormated = from.format(fromFormat)
      const toFormated = to.format(dateDisplayFormat)

      selected = [from.toDate(), to.toDate()]
      setDate(`${fromFormated} - ${toFormated}`)
    }

    if (dateType === 'range' && onChange) onChange(selected)
  }

  const handleSubmit = () => {
    if (dateType === 'single') {
      const submitted = handleSubmitSingle()
      if (!submitted) return
    }

    if (dateType === 'range') {
      handleSubmitRange()
    }

    handleClose()
  }

  useEffect(() => {
    if (Array.isArray(value)) {
      setDateRangeSelected((prev) => ({
        ...prev,
        from: value[0],
        to: value[1],
      }))
      const from = dayjs(value[0]).format(dateDisplayFormatFrom || dateDisplayFormat)

      const to = dayjs(value[1]).format(dateDisplayFormat)

      if (value[0] && value[1]) {
        setDate(`${from} - ${to}`)
      }
    } else if (value) {
      setDateSelected(value)
      setDate(dayjs(value).format(dateDisplayFormat))
    } else {
      setDate('')
    }
  }, [value, dateDisplayFormat, dateDisplayFormatFrom])

  return {
    isOpen,
    showDate,
    date,
    dateDefaultValue,
    anchorEl,
    menuSelected,
    dateSelected,
    dateRangeSelected,
    monthSelected,
    yearSelected,
    setMonthSelected,
    setYearSelected,
    handleOpen,
    handleClose,
    handleSelectMenu,
    handleSelectDate,
    handleSelectDateRange,
    handleReset,
    handleSubmit,
  }
}

export default useDatePicker
